import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import userService from 'services/userService';
import licenseService from 'services/licenseService';
import { canAddSharedLicenseMember } from '../../utils/licenseMembers';

import PageHeader from 'components/PageHeader';
import LoadingMark from 'components/ui/LoadingMark';
import { PAGE_CONTENT_CLASS } from '../../components/layout/PageContent';

import LicenseSummaryCard from './components/LicenseSummaryCard';
import MySubscriptionPanel from './components/MySubscriptionPanel';
import SharedUsersCard from './components/SharedUsersCard';
import RevokeLicenseDialog from './components/RevokeLicenseDialog';
import AddMemberDialog from './components/AddMemberDialog';

function formatNeoCoinsFromUser(user) {
  if (!user || typeof user !== 'object') return '0';
  const raw = user.neo_coins ?? user.neoCoins ?? user.neo_coin ?? user.neoCoin;
  if (raw == null || raw === '') return '0';
  const n = Number(raw);
  return Number.isFinite(n) ? String(Math.trunc(n)) : String(raw);
}

const LicenseOverview = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [optimisticMembers, setOptimisticMembers] = useState([]);

  const [memberToRevoke, setMemberToRevoke] = useState(null);
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);

  useEffect(() => {
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      setIsLoading(true);
      const result = await userService.getUserInfo();

      if (result?.success && result.data) {
        setUser(result.data);

        const backendEmails =
          result.data.activeLicense?.members?.map(m => m.email) || [];

        setOptimisticMembers(prev =>
          prev.filter(m => !backendEmails.includes(m.email))
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const license = user?.activeLicense;

  const isSharedOwnerView =
    license?.is_shared === true && Array.isArray(license.members);

  const canAddMember =
    isSharedOwnerView && canAddSharedLicenseMember(license);

  /* =========================
     OPTIMISTIC ADD MEMBER
  ========================= */
  const handleAddMember = async (email) => {
    try {
      await licenseService.shareLicenseWithUser({
        target_email: email,
      });

      // ✅ ONLY NOW do optimistic update
      setOptimisticMembers(prev => [
        ...prev,
        {
          user_id: `optimistic-${email}`,
          email,
          role: 'member',
          status: 'pending',
          added_at: new Date().toISOString(),
          documents_generated: 0,
          documents_signed: 0,
        },
      ]);

      setIsAddMemberOpen(false);

      // reconcile later
      await loadUserInfo();
    } catch (error) {
      console.error(error);
      throw error; // 👈 IMPORTANT: dialog will show it
    }
  };

  /* =========================
     REMOVE MEMBER
  ========================= */
  const handleRevokeMember = async () => {
    if (!memberToRevoke) return;

    setIsRevoking(true);
    try {
      await licenseService.removeMember({
        target_email: memberToRevoke.email,
      });

      toast.success('Licença revogada com sucesso.');
      setIsRevokeDialogOpen(false);
      setMemberToRevoke(null);
      await loadUserInfo();
    } catch (error) {
      console.error(error);
      toast.error(error?.message ?? 'Erro ao revogar licença.');
    } finally {
      setIsRevoking(false);
    }
  };

  const handleReshareMember = async (member) => {
    if (!member?.email) return;

    await licenseService.shareLicenseWithUser({
      target_email: member.email,
    });

    await loadUserInfo();
  };

  const mergedMembers = [
    ...(license?.members || []),
    ...optimisticMembers,
  ];

  return (
    <div className={PAGE_CONTENT_CLASS}>
        <PageHeader
          title="Minha Licença"
          subtitle="Licença ativa, assinatura mensal e Neo Coins"
          icon="ShieldCheck"
        />

        {/* LOADING */}
        {isLoading && (
          <div className="flex justify-center py-10">
            <LoadingMark message="Carregando informações da licença..." />
          </div>
        )}

        {!isLoading && (
          <div className="space-y-6">
            <MySubscriptionPanel neoCoinsLabel={formatNeoCoinsFromUser(user)} />

            {!license && (
              <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-sm">
                <p className="text-sm text-muted-foreground">
                  Sem licença ativa no momento. Adquira um plano em Comprar Licença
                  (compra única ou assinatura mensal).
                </p>
              </div>
            )}

            {license && (
              <>
                <LicenseSummaryCard
                  license={license}
                  neoCoinsLabel={formatNeoCoinsFromUser(user)}
                />

                {isSharedOwnerView && (
                  <SharedUsersCard
                    members={mergedMembers}
                    maxMembers={license.max_members}
                    canAddMember={canAddMember}
                    onAddMember={() => setIsAddMemberOpen(true)}
                    onRevoke={(member) => {
                      setMemberToRevoke(member);
                      setIsRevokeDialogOpen(true);
                    }}
                    onReshare={handleReshareMember}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* DIALOGS */}
        <RevokeLicenseDialog
          isOpen={isRevokeDialogOpen}
          member={memberToRevoke}
          onCancel={() => {
            setIsRevokeDialogOpen(false);
            setMemberToRevoke(null);
          }}
          onConfirm={handleRevokeMember}
          isConfirming={isRevoking}
        />

        <AddMemberDialog
          isOpen={isAddMemberOpen}
          onCancel={() => setIsAddMemberOpen(false)}
          onConfirm={handleAddMember}
        />
      </div>
  );
};

export default LicenseOverview;